import React, { memo, forwardRef, type Ref, useRef } from 'react';
import ReactDOM from 'react-dom';
import { styled, css } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import DragHandleVerticalIcon from '@atlaskit/icon/core/migration/drag-handle-vertical--drag-handler';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	TAB_HEIGHT,
	TAB_UNDERLINE_HEIGHT,
	TAB_ANGLE,
} from '@atlassian/jira-navigation-kit-common/src/index.tsx';
import BeforeTab from '../common/ui/before-tab/index.tsx';
import { useDraggableTabs } from '../controllers/draggable-tabs/index.tsx';
import type { TabProps } from '../types.tsx';
import AnchorTab from './anchor-tab/index.tsx';
import ButtonTab from './button-tab/index.tsx';
import DropIndicator from './drop-indicator/index.tsx';
import LinkTab from './link-tab/index.tsx';
import StaticTab from './static-tab/index.tsx';

const Tab = memo(
	forwardRef((props: TabProps, outerRef: Ref<HTMLElement>) => {
		/*
		 * Warning - if you add a new prop to this component make sure you add it
		 * to the list of ignored destructured props in props.type === 'dropdown-trigger'
		 * block down below or you will get the error
		 * "React does not recognize the `yourNewProp` prop on a DOM element."
		 */
		const {
			name,
			onClick,
			href,
			isSelected = false,
			isLoading = false,
			type = 'router-link',
			elemBefore,
			elemAfter,
			isDraggable = false,
			position,
			id,
			menu,
			handleDrag,
			className,
		} = props;

		const elementRef = useRef(null);
		const draggableRef = useRef(null);

		const { closestEdge, dragState } = useDraggableTabs({
			isDragEnabled: isDraggable,
			position,
			id,
			handleDrag,
			isSelected,
			elementRef,
			draggableRef,
		});
		const isDragging = Boolean(dragState && dragState.type === 'dragging');

		const commonAfterElements = (
			<>
				{elemAfter}
				<DropIndicator
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-kit-ui-tab.ui.drag-indicator"
					closestEdge={closestEdge}
				/>
				{dragState && dragState.type === 'preview'
					? ReactDOM.createPortal(
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							<DraggablePreview data-testid="navigation-kit-ui-tab.ui.drag-preview">
								<DragHandleVerticalIcon label="" LEGACY_size="small" />
								<span>{name}</span>
								{elemAfter}
							</DraggablePreview>,
							dragState.container,
						)
					: null}
			</>
		);

		if (isLoading || (isSelected && type !== 'dropdown-trigger')) {
			return (
				<NonInteractiveTab
					as={isSelected ? 'h2' : undefined}
					aria-current={isSelected ? 'page' : undefined}
					tabIndex={0}
					isDraggable={isDraggable}
					isDragging={isDragging}
					ref={(r) => {
						if (outerRef && typeof outerRef === 'object') {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, no-param-reassign
							(outerRef as React.MutableRefObject<HTMLElement | null>).current = r;
						}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(elementRef as React.MutableRefObject<HTMLElement | null>).current = r;
					}}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="navigation-kit-ui-tab.ui.non-interactive-tab"
				>
					<BeforeTab
						elementBefore={elemBefore}
						isDraggable={isDraggable}
						draggableRef={draggableRef}
					/>
					<span>{name}</span>
					{!isLoading && menu}
					{commonAfterElements}
				</NonInteractiveTab>
			);
		}

		if (type === 'anchor') {
			return (
				<TabAnchor
					href={href}
					onClick={onClick}
					isDraggable={isDraggable}
					isDragging={isDragging}
					ref={elementRef}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="navigation-kit-ui-tab.ui.tab-anchor"
				>
					<BeforeTab
						elementBefore={elemBefore}
						isDraggable={isDraggable}
						draggableRef={draggableRef}
					/>
					<span>{name}</span>
					{commonAfterElements}
				</TabAnchor>
			);
		}

		if (props.type === 'dropdown-trigger') {
			const {
				name: _name,
				onClick: _onClick,
				href: _href,
				isSelected: _isSelected,
				isLoading: _isLoading,
				type: _type,
				elemAfter: _elemAfter,
				elemBefore: _elemBefore,
				handleDrag: _handleDrag,
				menu: _menu,
				testId,
				...buttonHTMLProps
			} = props;
			return (
				<Box
					xcss={boxStyles}
					ref={elementRef}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-kit-ui-tab.ui.tab-button"
				>
					<TabButton
						data-testid={testId}
						// @ts-expect-error - TS2322
						ref={outerRef}
						{...buttonHTMLProps}
						aria-current={isSelected ? 'page' : undefined}
						type="button"
						onClick={onClick}
						isDraggable={isDraggable}
						isDragging={isDragging}
					>
						<BeforeTab
							elementBefore={elemBefore}
							isDraggable={isDraggable}
							draggableRef={draggableRef}
						/>
						{name}
						<ChevronContainer>
							<ChevronDownIcon spacing="spacious" label="" size="small" />
						</ChevronContainer>
						{commonAfterElements}
					</TabButton>
				</Box>
			);
		}

		return (
			<TabLink
				to={href}
				onClick={onClick}
				isDraggable={isDraggable}
				isDragging={isDragging}
				prefetch="hover"
				ref={elementRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={className}
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="navigation-kit-ui-tab.ui.tab-link"
			>
				<BeforeTab
					elementBefore={elemBefore}
					isDraggable={isDraggable}
					draggableRef={draggableRef}
				/>
				<span>{name}</span>
				{commonAfterElements}
			</TabLink>
		);
	}),
);

const themedUnderline = {
	'&::after': {
		position: 'absolute',
		bottom: token('space.0'),
		left: token('space.0'),
		right: token('space.0'),
		content: "''",
		height: token('space.025'),
		maxWidth: '100%',
		borderRadius: `${TAB_UNDERLINE_HEIGHT / 2}px`,
		backgroundColor: Tokens.COLOR_LINK,
	},
};
const themedUnderlinePressed = {
	'&::after': {
		...themedUnderline['&::after'],
		backgroundColor: `${Tokens.COLOR_LINK_PRESSED}`,
	},
};

const tabStyles = css<{ isDraggable?: boolean; isDragging?: boolean }>({
	display: 'flex',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.050'),
	margin: 0,
	textDecoration: 'none',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${TAB_HEIGHT}px`,
	boxSizing: 'border-box',
	minWidth: 'max-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT_SUBTLE,
	alignItems: 'center',
	gap: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"&[aria-expanded='true'], &[aria-current='page']": {
		display: 'flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: `${Tokens.COLOR_LINK}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...themedUnderline,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:hover, &:focus:not(:focus-visible), &:active': {
		outline: 'none',
	},
	'&:hover, &:focus-visible, &:focus': {
		outlineOffset: token('space.negative.025'),
		boxShadow: 'none',
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: Tokens.COLOR_LINK,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...themedUnderline,
	},
	'&:active': {
		outlineOffset: token('space.negative.025'),
		boxShadow: 'none',
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: Tokens.COLOR_LINK_PRESSED,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...themedUnderlinePressed,
	},
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
	},
	/* stylelint-disable-next-line property-no-unknown */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'&:hover': ({ isDraggable }) => ({
		'--display-icon-before': isDraggable ? 'none' : 'flex',
		'--display-drag-handle': isDraggable ? 'flex' : 'none',
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDragging }: { isDragging: boolean }) =>
		isDragging
			? {
					opacity: 0.1,
				}
			: {},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const NonInteractiveTab = styled(StaticTab)(tabStyles);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const TabLink = styled(LinkTab)(tabStyles);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const TabAnchor = styled(AnchorTab)(tabStyles);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const TabButton = styled(ButtonTab)(tabStyles, {
	border: 'none',
	backgroundColor: 'transparent',
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	paddingRight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChevronContainer = styled.span({
	marginRight: token('space.negative.050'),
});

const previewStyles = css({
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
	borderRight: `${token('border.width')} solid ${token('color.border')}`,
	borderTopLeftRadius: token('border.radius.200'),
	borderTopRightRadius: token('border.radius.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: Tokens.COLOR_ELEVATION_SURFACE,
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.050'),
	height: '30px',
	marginTop: token('space.100'),
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	rotate: `${TAB_ANGLE}deg`,
	'--display-icon-before': 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...themedUnderline,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- To migrate as part of go/ui-styling-standard
const DraggablePreview = styled.div({ ...tabStyles, ...previewStyles });

const boxStyles = xcss({
	minWidth: 'max-content',
});

export default Tab;
